import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Badge, Flex, IconButton, TextArea } from "@radix-ui/themes";
import { ArrowUpIcon, UploadIcon, Cross1Icon } from "@radix-ui/react-icons";
import { useMediaQuery } from "react-responsive";

interface InputProps {
  placeholder?: string;
  allowUploadFile: boolean;
  disabled?: boolean;
  sendMessage: (message: string, file?: File) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  predefinedMessage?: string;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
}

export function Input({
  placeholder = "Reply...",
  allowUploadFile,
  predefinedMessage,
  disabled,
  sendMessage,
  uploadFile,
  style,
  onFocus,
  onBlur,
}: InputProps) {
  const isSmallScreen = useMediaQuery({ query: "(max-width: 520px)" });

  const [message, setMessage] = useState(predefinedMessage ?? "");
  const [sending, setSending] = useState(false);

  const [files, setFiles] = useState<File[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const sendDisabled = useMemo(() => {
    const isInputEmpty = message.length === 0 || message.trim() === "";
    const isFileEmpty = files.length === 0;
    const isEmpty = isInputEmpty && isFileEmpty;

    return isEmpty || sending || disabled;
  }, [disabled, message, files, sending]);

  const handleSendMessage = useCallback(async () => {
    if (sendDisabled) {
      return;
    }

    setSending(true);

    const messageValue = message;
    const filesValue = [...files];

    setMessage("");
    setFiles([]);

    const shouldSendMessage = messageValue.trim() !== "";
    const shouldUploadFiles = filesValue.length > 0;

    if (shouldSendMessage && shouldUploadFiles && filesValue.length === 1) {
      await sendMessage(message, filesValue[0]);

      // Clear the message and files after sending
      setSending(false);
      return;
    }

    const uploadFilePromises = shouldUploadFiles
      ? files.map((file) => uploadFile(file))
      : [];

    await Promise.allSettled([
      ...uploadFilePromises,
      ...(shouldSendMessage ? [sendMessage(message)] : []),
    ]);

    // Clear the message and files after sending
    setSending(false);

    textareaRef.current?.focus();
  }, [sendDisabled, message, sendMessage, files, uploadFile]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      if (!isMobile && event.key === "Enter" && !event.shiftKey) {
        handleSendMessage();
        event.preventDefault();
      }
    },
    [handleSendMessage]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    },
    []
  );

  const handleFileUpload = useCallback(() => {
    if (allowUploadFile) {
      const input = document.createElement("input");
      input.type = "file";
      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
          setFiles((prevFiles) => [...prevFiles, file]);
        }
      };
      input.click();
    }
  }, [allowUploadFile]);

  const handleRemoveFile = useCallback((file: File) => {
    setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile !== file));
  }, []);

  const setTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    el?.focus();
  }, []);

  useEffect(() => {
    // Reset the height to get the new scrollHeight
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      // Set the height to match the scrollHeight
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [message]);

  return (
    <Flex py={"1"} px={"2"} direction={"column"} style={style}>
      <Flex py={"2"} px={"3"} gap={"2"} justify={"between"} align={"end"}>
        <Flex direction={"column"} gap={"1"} flexGrow={"1"}>
          <TextArea
            placeholder={placeholder}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            style={{
              flexGrow: "1",
              boxShadow: "none",
              outline: "none",
            }}
            className="qc-Chat__Input"
            ref={setTextareaRef}
            size={isSmallScreen ? "3" : "2"}
            aria-label={placeholder}
          />

          {allowUploadFile && (
            <Flex gap={"2"} align={"center"} minHeight={"20px"}>
              {files.length === 0 && (
                <IconButton
                  size={"1"}
                  color="gray"
                  variant="ghost"
                  onClick={handleFileUpload}
                  aria-label="Upload file"
                >
                  <UploadIcon width="15" height="15" />
                </IconButton>
              )}

              {files.map((file) => (
                <Badge key={file.name} size="1">
                  {file.name.slice(0, 10)}...
                  <IconButton
                    size="1"
                    color="gray"
                    variant="ghost"
                    onClick={() => handleRemoveFile(file)}
                    aria-label="Remove file"
                  >
                    <Cross1Icon width="10" height="10" />
                  </IconButton>
                </Badge>
              ))}
            </Flex>
          )}
        </Flex>

        <IconButton
          color="gray"
          variant="solid"
          radius="full"
          onClick={handleSendMessage}
          disabled={sendDisabled}
          loading={sending}
          className="qc-Chat__CTAButton"
          aria-label="Send message"
        >
          <ArrowUpIcon width="15" height="15" />
        </IconButton>
      </Flex>
    </Flex>
  );
}
